import React, { useState, useMemo, useRef, useEffect } from 'react'
import { library }                                      from '@fortawesome/fontawesome-svg-core'
import { fas }                                          from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon }                              from '@fortawesome/react-fontawesome'
import { postRequest, patchRequest, deleteRequest }     from '../helpers/fetch'
import HowItWorks                                       from './HowItWorks'
import DraftRecordsModal                                from './DraftRecordsModal'

library.add(fas)

// A checkbox with a label + hint, both indented to the same x-offset.
// Renders Yuman's standard `.checkbox.checkbox-primary` markup so it inherits
// the project's checkbox visuals; the hint sits inside the <label> so it auto-aligns
// with the label text. Inline styles keep the hint un-bold and aligned with the
// 26px checkbox box.
const CheckboxWithHint = ({ id, label, hint, checked, onChange }) => (
  <div className='checkbox checkbox-primary vwf-checkbox mb-4'>
    <input
      id       = {id}
      type     = 'checkbox'
      checked  = {!!checked}
      onChange = {e => onChange(e.target.checked)}
    />
    <label htmlFor={id}>
      <span className='block font-semibold text-gray-900' style={{ lineHeight: '26px' }}>{label}</span>
      {hint && (
        <span
          className='block text-xs text-gray-500 font-normal mt-1'
          style={{ lineHeight: 1.4 }}
        >
          {hint}
        </span>
      )}
    </label>
  </div>
)

let nextTempId = -1
const tempId = () => nextTempId--

const ApproverSelect = ({ users, selectedIds, onChange, placeholder }) => {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState('')
  const wrapperRef        = useRef(null)

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return undefined
    const handler = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const idSet = new Set(selectedIds)
  const remaining = users
    .filter(u => !idSet.has(u.id))
    .filter(u => !query.trim() || (u.name || '').toLowerCase().includes(query.trim().toLowerCase()))

  const remove = id => onChange(selectedIds.filter(i => i !== id))
  const add    = id => { onChange([...selectedIds, id]); setQuery(''); setOpen(false) }

  const usersById = users.reduce((acc, u) => { acc[u.id] = u; return acc }, {})

  return (
    <div ref={wrapperRef}>
      <div className='flex flex-wrap gap-1.5 mb-1.5'>
        {selectedIds.map(id => (
          <span key={id} className='inline-flex items-center bg-indigo-100 text-indigo-800 px-2.5 py-[3px] rounded-xl text-[13px]'>
            {(usersById[id] || {}).name || `#${id}`}
            <button
              type='button'
              className='bg-transparent border-0 text-indigo-700 ml-1 cursor-pointer text-base pl-0.5'
              onClick={() => remove(id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className='relative'>
        <input
          type        = 'text'
          className   = 'form-control form-control-sm'
          value       = {query}
          onFocus     = {() => setOpen(true)}
          onChange    = {e => { setQuery(e.target.value); setOpen(true) }}
          placeholder = {placeholder || I18n.t('validation_workflows.form.add_approver')}
        />
        {open && (
          remaining.length > 0 ? (
            <div className='absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-md max-h-[220px] overflow-y-auto z-10'>
              {remaining.slice(0, 50).map(u => (
                <div
                  key       = {u.id}
                  className = 'px-3 py-2 cursor-pointer text-sm hover:bg-gray-100'
                  onClick   = {() => add(u.id)}
                >
                  {u.name} <span className='text-muted'>{u.email}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className='absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-md max-h-[220px] overflow-y-auto z-10'>
              <div className='px-3 py-2 cursor-pointer text-sm text-muted'>
                {I18n.t('validation_workflows.form.no_eligible_users')}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}

const CategorySelect = ({ categories, selectedIds, onChange }) => {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState('')
  const wrapperRef        = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const handler = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const idSet     = new Set(selectedIds)
  const byId      = categories.reduce((acc, c) => { acc[c.id] = c; return acc }, {})
  const remaining = categories
    .filter(c => !idSet.has(c.id))
    .filter(c => !query.trim() || (c.name || '').toLowerCase().includes(query.trim().toLowerCase()))

  const remove = id => onChange(selectedIds.filter(i => i !== id))
  const add    = id => { onChange([...selectedIds, id]); setQuery(''); setOpen(false) }

  return (
    <div ref={wrapperRef}>
      <div className='flex flex-wrap gap-1.5 mb-1.5'>
        {selectedIds.map(id => (
          <span key={id} className='inline-flex items-center bg-indigo-100 text-indigo-800 px-2.5 py-[3px] rounded-xl text-[13px]'>
            {(byId[id] || {}).name || `#${id}`}
            <button
              type='button'
              className='bg-transparent border-0 text-indigo-700 ml-1 cursor-pointer text-base pl-0.5'
              onClick={() => remove(id)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className='relative'>
        <input
          type        = 'text'
          className   = 'form-control form-control-sm'
          value       = {query}
          onFocus     = {() => setOpen(true)}
          onChange    = {e => { setQuery(e.target.value); setOpen(true) }}
          placeholder = {I18n.t('validation_workflows.form.categories_all_placeholder')}
        />
        {open && (
          <div className='absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-md max-h-[220px] overflow-y-auto z-10'>
            {remaining.length > 0 ? (
              remaining.slice(0, 100).map(c => (
                <div
                  key       = {c.id}
                  className = 'px-3 py-2 cursor-pointer text-sm hover:bg-gray-100 whitespace-pre'
                  onClick   = {() => add(c.id)}
                >
                  {c.parentId ? `— ${c.name}` : c.name}
                </div>
              ))
            ) : (
              <div className='px-3 py-2 text-sm text-muted'>{I18n.t('validation_workflows.form.no_categories_match')}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const AmountInput = ({ value, onChange, currencySymbol, placeholder, min }) => (
  <div className='relative block'>
    <input
      type        = 'number'
      min         = {min != null ? min : 0}
      step        = '0.01'
      className   = 'form-control pr-8'
      value       = {value != null ? value : ''}
      onChange    = {onChange}
      placeholder = {placeholder}
    />
    <span className='absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 pointer-events-none text-sm'>
      {currencySymbol}
    </span>
  </div>
)

// Returns null when the step has at least one approver authorised at the
// minimum amount this step would ever see (i.e. max(trigger, step.min)) and
// therefore wouldn't be auto-skipped at runtime; otherwise returns the i18n
// key for the warning to display.
const authorityWarning = (step, users, triggerAmountCents) => {
  if (!step.approverIds || step.approverIds.length === 0) return null
  const threshold = Math.max(step.minAmountCents || 0, triggerAmountCents || 0)
  if (!threshold) return null

  const byId    = users.reduce((acc, u) => { acc[u.id] = u; return acc }, {})
  const covered = step.approverIds.some(id => {
    const u = byId[id]
    if (!u) return false
    return u.unlimitedApprover || u.approvalLimitAmountCents >= threshold
  })
  return covered ? null : 'validation_workflows.errors.step_no_authorised_approver'
}

const checksOf = step => (step.checksText ?? (step.checks || []).join('\n')).split('\n').map(c => c.trim()).filter(Boolean)

const StepCard = ({
  step, index, total, users, currencySymbol, triggerAmountCents, amountBased, namedChecks,
  onChange, onMoveUp, onMoveDown, onDelete
}) => {
  const update  = (patch) => onChange({ ...step, ...patch })
  const warning = amountBased ? authorityWarning(step, users, triggerAmountCents) : null

  return (
    <div className='border border-gray-200 rounded-md px-4 py-3.5 bg-gray-50'>
      <div className='flex items-center gap-3 mb-3'>
        <span className='bg-gray-100 text-gray-900 font-semibold px-2.5 py-0.5 rounded-[10px] text-[13px]'>
          {I18n.t('validation_workflows.form.step_label', { n: index + 1 })}
        </span>
        <span className='text-muted'>
          {I18n.t('validation_workflows.form.approvers_count', { count: (step.approverIds || []).length })}
        </span>
        <div className='ml-auto flex'>
          <button type='button' className='btn btn-link btn-sm' disabled={index === 0} onClick={onMoveUp}>
            <FontAwesomeIcon icon='chevron-up' />
          </button>
          <button type='button' className='btn btn-link btn-sm' disabled={index === total - 1} onClick={onMoveDown}>
            <FontAwesomeIcon icon='chevron-down' />
          </button>
          <button type='button' className='btn btn-link btn-sm text-danger' onClick={onDelete}>
            <FontAwesomeIcon icon='trash' />
          </button>
        </div>
      </div>

      <div className='form-group'>
        <label>{I18n.t('validation_workflows.form.name')} <span className='text-danger'>*</span></label>
        <input
          type='text'
          className='form-control'
          value={step.name || ''}
          onChange={e => update({ name: e.target.value })}
          placeholder={I18n.t('validation_workflows.form.step_name_placeholder')}
        />
      </div>

      {amountBased && (
      <div className='form-group flex gap-3'>
        <div className='flex-1'>
          <label>{I18n.t('validation_workflows.form.min_amount')}</label>
          <AmountInput
            value={step.minAmountCents != null ? step.minAmountCents / 100 : 0}
            min={0}
            currencySymbol={currencySymbol}
            onChange={e => update({
              minAmountCents: e.target.value === '' ? 0 : Math.round(parseFloat(e.target.value) * 100)
            })}
          />
        </div>
        <div className='flex-1'>
          <label>{I18n.t('validation_workflows.form.max_amount')}</label>
          <AmountInput
            value={step.maxAmountCents != null ? step.maxAmountCents / 100 : ''}
            placeholder={I18n.t('validation_workflows.form.no_limit')}
            currencySymbol={currencySymbol}
            onChange={e => update({
              maxAmountCents: e.target.value === '' ? null : Math.round(parseFloat(e.target.value) * 100)
            })}
          />
        </div>
      </div>
      )}

      {namedChecks && (
        <div className='form-group'>
          <label>{I18n.t('validation_workflows.form.checks')}</label>
          <textarea
            className='form-control'
            rows={3}
            value={step.checksText ?? (step.checks || []).join('\n')}
            onChange={e => update({ checksText: e.target.value })}
            placeholder={I18n.t('validation_workflows.form.checks_placeholder')}
          />
          <div className='text-muted small mt-1'>{I18n.t('validation_workflows.form.checks_help')}</div>
        </div>
      )}

      <div className='form-group'>
        <label>{I18n.t('validation_workflows.form.approvers')} <span className='text-danger'>*</span></label>
        <ApproverSelect
          users={users}
          selectedIds={step.approverIds || []}
          onChange={ids => update({ approverIds: ids })}
        />
        {warning && (
          <div className='mt-1.5 px-2.5 py-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2'>
            <FontAwesomeIcon icon='exclamation-triangle' className='mt-0.5 shrink-0' />
            <span>{I18n.t(warning)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const Form = ({
  workflow: initialWorkflow,
  users,
  categories,
  objectType,
  namedChecks,
  currencySymbol,
  submitPath,
  submitMethod,
  backPath,
  deletePath,
  canDelete
}) => {
  const [name,         setName]         = useState(initialWorkflow.name || '')
  const [trigger,      setTrigger]      = useState(initialWorkflow.triggerAmountCents || 0)
  const [active,       setActive]       = useState(!!initialWorkflow.active)
  const [selfApproval, setSelfApproval] = useState(!!initialWorkflow.selfApproval)
  const [allowEdit,    setAllowEdit]    = useState(!!initialWorkflow.allowEditAfterValidation)
  const [categoryIds,  setCategoryIds]  = useState(initialWorkflow.categoryIds || [])
  const [steps,        setSteps]        = useState(
    (initialWorkflow.approvalSteps || [])
      .slice()
      .sort((a, b) => a.position - b.position)
      .map(s => ({ ...s, _destroy: false }))
  )
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)
  const [caughtDraftsCount, setCaughtDraftsCount] = useState(0)

  const visibleSteps      = useMemo(() => steps.filter(s => !s._destroy), [steps])
  const amountBased       = objectType !== 'workorder'
  const availableCategories = categories || []
  const supportsCategories  = availableCategories.length > 0

  const updateStep = (index, newStep) => {
    setSteps(prev => {
      const visible = prev.filter(s => !s._destroy)
      const target  = visible[index]
      return prev.map(s => (s === target ? newStep : s))
    })
  }

  const moveStep = (index, dir) => {
    setSteps(prev => {
      const visible  = prev.filter(s => !s._destroy)
      const swapWith = visible[index + dir]
      if (!swapWith) return prev
      const a = visible[index]
      return prev.map(s => {
        if (s === a)        return { ...swapWith, position: a.position }
        if (s === swapWith) return { ...a,        position: swapWith.position }
        return s
      })
    })
  }

  const deleteStep = index => {
    setSteps(prev => {
      const visible = prev.filter(s => !s._destroy)
      const target  = visible[index]
      if (target.id) {
        return prev.map(s => (s === target ? { ...s, _destroy: true } : s))
      }
      return prev.filter(s => s !== target)
    })
  }

  const addStep = () => {
    const lastPos = visibleSteps.reduce((max, s) => Math.max(max, s.position || 0), 0)
    setSteps(prev => [...prev, {
      id:               null,
      tempId:           tempId(),
      name:             '',
      position:         lastPos + 1,
      minAmountCents:   0,
      maxAmountCents:   null,
      approverIds:      [],
      _destroy:         false
    }])
  }

  const validate = () => {
    const errs = []
    if (!name.trim()) errs.push(I18n.t('validation_workflows.errors.name_missing'))
    if (active && visibleSteps.length === 0) {
      errs.push(I18n.t('validation_workflows.errors.no_steps'))
    }
    visibleSteps.forEach((step, i) => {
      if (!(step.name || '').trim()) {
        errs.push(I18n.t('validation_workflows.errors.step_name_missing', { n: i + 1 }))
      }
      if (!(step.approverIds || []).length) {
        errs.push(I18n.t('validation_workflows.errors.step_approvers_missing', { n: i + 1 }))
      }
    })
    return errs
  }

  const onSubmit = async draftRecords => {
    const errs = validate()
    setErrors(errs)
    if (errs.length > 0) return

    setSaving(true)
    const payload = {
      validation_workflow: {
        name,
        object_type: objectType,
        trigger_amount_cents: trigger,
        active,
        self_approval: selfApproval,
        allow_edit_after_validation: allowEdit,
        category_ids: supportsCategories ? categoryIds : [],
        approval_steps_attributes: steps.map(s => {
          const visibleIndex = steps.filter(x => !x._destroy).indexOf(s)
          return {
            id:               s.id || undefined,
            name:             s.name,
            position:         s._destroy ? s.position : (visibleIndex + 1),
            min_amount_cents: s.minAmountCents || 0,
            max_amount_cents: s.maxAmountCents,
            approver_ids:     s.approverIds || [],
            checks:           checksOf(s),
            _destroy:         s._destroy ? '1' : undefined
          }
        })
      },
      draft_records: draftRecords
    }

    try {
      const fn = submitMethod === 'patch' ? patchRequest : postRequest
      const res = await fn({ url: submitPath, body: payload })
      if (res && res.caught_drafts_count) {
        setSaving(false)
        setCaughtDraftsCount(res.caught_drafts_count)
        return
      }
      if (res && Array.isArray(res.errors) && res.errors.length > 0) {
        setSaving(false)
        setCaughtDraftsCount(0)
        setErrors(res.errors)
        return
      }
      window.location.href = (res && res.redirect_to) || backPath
    } catch (e) {
      setSaving(false)
      setCaughtDraftsCount(0)
      setErrors([I18n.t('validation_workflows.errors.save_failed')])
    }
  }

  const onCancel = () => {
    window.location.href = backPath
  }

  const onDelete = () => {
    if (!window.confirm(I18n.t('validation_workflows.confirm_delete'))) return
    deleteRequest(deletePath).then(res => {
      if (res && Array.isArray(res.errors) && res.errors.length > 0) {
        setErrors(res.errors)
        return
      }
      window.location.href = backPath
    })
  }

  return (
    <div className='py-6 px-8 max-w-[880px] mx-auto'>
      <div className='flex justify-between items-start mb-4'>
        <div>
          <h3 className='m-0 font-bold'>{name || I18n.t('validation_workflows.form.new_title')}</h3>
          <div className='text-muted'>
            {active
              ? I18n.t('validation_workflows.form.active_subtitle')
              : I18n.t('validation_workflows.form.inactive_subtitle')}
          </div>
        </div>
        <div className='flex gap-[5px] shrink-0'>
          <div className='btn btn-sm btn-default' onClick={onCancel}>
            {I18n.t('button.cancel')}
          </div>
          <div
            className = {`btn btn-sm btn-primary ${saving ? 'disabled' : ''}`}
            onClick   = {() => { if (!saving) onSubmit() }}
          >
            {I18n.t('button.save')}
          </div>
        </div>
      </div>

      {errors.length > 0 && (
        <div className='mb-4'>
          {errors.map((err, i) => (
            <div key={i} className='alert alert-danger'>
              <FontAwesomeIcon icon='exclamation-triangle' /> {err}
            </div>
          ))}
        </div>
      )}

      <section className='bg-white border border-gray-200 rounded-lg px-6 py-5 mb-4'>
        <h5 className='font-bold mt-0 mb-4'>{I18n.t('validation_workflows.form.settings')}</h5>
        <div className='form-group'>
          <label>{I18n.t('validation_workflows.form.name')} <span className='text-danger'>*</span></label>
          <input
            type='text'
            className='form-control'
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={I18n.t('validation_workflows.form.name_placeholder')}
          />
        </div>
        {amountBased && (
        <div className='form-group'>
          <label className='block font-semibold text-gray-900 mb-0.5'>{I18n.t('validation_workflows.form.trigger_amount')}</label>
          <div className='block text-xs text-gray-500 mb-2'>{I18n.t('validation_workflows.form.trigger_amount_help')}</div>
          <div className='max-w-[240px]'>
            <AmountInput
              value={trigger / 100}
              currencySymbol={currencySymbol}
              onChange={e => setTrigger(e.target.value === '' ? 0 : Math.round(parseFloat(e.target.value) * 100))}
            />
          </div>
        </div>
        )}

        {supportsCategories && (
        <div className='form-group'>
          <label className='block font-semibold text-gray-900 mb-0.5'>{I18n.t('validation_workflows.form.categories')}</label>
          <div className='block text-xs text-gray-500 mb-2'>{I18n.t('validation_workflows.form.categories_help')}</div>
          <CategorySelect
            categories={availableCategories}
            selectedIds={categoryIds}
            onChange={setCategoryIds}
          />
        </div>
        )}

        <CheckboxWithHint
          id       = 'vwf-active'
          label    = {I18n.t('validation_workflows.form.active')}
          hint     = {I18n.t('validation_workflows.form.active_hint')}
          checked  = {active}
          onChange = {checked => setActive(checked)}
        />

        <CheckboxWithHint
          id       = 'vwf-self-approval'
          label    = {I18n.t('validation_workflows.form.self_approval')}
          hint     = {selfApproval
            ? I18n.t('validation_workflows.form.self_approval_on_hint')
            : I18n.t('validation_workflows.form.self_approval_off_hint')}
          checked  = {selfApproval}
          onChange = {setSelfApproval}
        />

        <CheckboxWithHint
          id       = 'vwf-allow-edit'
          label    = {I18n.t('validation_workflows.form.allow_edit_after_validation')}
          hint     = {allowEdit
            ? I18n.t('validation_workflows.form.allow_edit_on_hint')
            : I18n.t('validation_workflows.form.allow_edit_off_hint')}
          checked  = {allowEdit}
          onChange = {setAllowEdit}
        />
      </section>

      <section className='bg-white border border-gray-200 rounded-lg px-6 py-5 mb-4'>
        <h5 className='m-0 font-bold'>{I18n.t('validation_workflows.form.approval_steps')}</h5>
        <div className='text-muted small mb-3'>{I18n.t('validation_workflows.form.approval_steps_help')}</div>

        {visibleSteps.length === 0 ? (
          <div className='px-5 py-10 text-center text-gray-400 border border-dashed border-gray-200 rounded-lg'>
            <FontAwesomeIcon icon='users' />
            <p className='my-3'>{I18n.t('validation_workflows.form.no_steps_yet')}</p>
          </div>
        ) : (
          <div className='flex flex-col gap-3 mt-3'>
            {visibleSteps.map((step, idx) => (
              <StepCard
                key                = {step.id || step.tempId || idx}
                step               = {step}
                index              = {idx}
                total              = {visibleSteps.length}
                users              = {users}
                currencySymbol     = {currencySymbol}
                triggerAmountCents = {trigger}
                amountBased        = {amountBased}
                namedChecks        = {namedChecks}
                onChange           = {newStep => updateStep(idx, newStep)}
                onMoveUp           = {() => moveStep(idx, -1)}
                onMoveDown         = {() => moveStep(idx, +1)}
                onDelete           = {() => deleteStep(idx)}
              />
            ))}
          </div>
        )}

        <div className='d-flex j-c-end mt-3'>
          <a className='small text-pointer' onClick={addStep}>
            <FontAwesomeIcon icon='plus-circle' className='mr-2' />
            {I18n.t('validation_workflows.form.add_step')}
          </a>
        </div>
      </section>

      <section className='bg-white border border-gray-200 rounded-lg px-6 py-5 mb-4'>
        <HowItWorks
          workflow={{
            triggerAmountCents:      trigger,
            selfApproval,
            allowEditAfterValidation: allowEdit,
            approvalSteps:            visibleSteps,
            categoryIds:             supportsCategories ? categoryIds : []
          }}
          users={users}
          categories={availableCategories}
          objectType={objectType}
          currencySymbol={currencySymbol}
        />
      </section>

      <DraftRecordsModal
        count      = {caughtDraftsCount}
        objectType = {objectType}
        saving     = {saving}
        onCancel   = {() => setCaughtDraftsCount(0)}
        onConfirm  = {onSubmit}
      />

      {canDelete && (
        <div className='flex justify-end mt-1'>
          <a className='text-danger small text-pointer' onClick={onDelete}>
            <FontAwesomeIcon icon='trash' className='mr-1' />
            {I18n.t('validation_workflows.form.delete_workflow')}
          </a>
        </div>
      )}
    </div>
  )
}

export default Form
